import { useId, type InputHTMLAttributes, type Ref } from 'react';
import { cn } from '@/lib/cn';

type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & {
  label: string;
  hint?: string;
  error?: string | undefined;
  ref?: Ref<HTMLInputElement>;
};

/** Label above, 44px input, hint and error text wired with aria-describedby (DESIGN.md §4 Field). */
export function Field({ label, hint, error, className, ref, ...input }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint && hintId, error && errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        ref={ref}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          'min-h-11 w-full rounded-md bg-surface px-3 text-body-app text-ink placeholder:text-ink-subtle',
          'disabled:cursor-not-allowed disabled:opacity-40',
          error ? 'border-2 border-danger' : 'border border-line-strong',
        )}
        {...input}
      />
      {hint && !error && (
        <p id={hintId} className="m-0 text-meta text-ink-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="m-0 text-meta text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
